import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const GPACalculatorPage = () => {
  const [courses, setCourses] = useState([
    { name: '', grade: 'A', credits: '' },
    { name: '', grade: 'A', credits: '' },
    { name: '', grade: 'A', credits: '' },
    { name: '', grade: 'A', credits: '' },
    { name: '', grade: 'A', credits: '' }
  ]);
  const [result, setResult] = useState(null);

  const gradePoints = {
    'A+': 4.0, 'A': 4.0, 'A-': 3.7,
    'B+': 3.3, 'B': 3.0, 'B-': 2.7,
    'C+': 2.3, 'C': 2.0, 'C-': 1.7,
    'D+': 1.3, 'D': 1.0, 'D-': 0.7,
    'F': 0.0
  };

  const updateCourse = (index, field, value) => {
    const newCourses = [...courses];
    newCourses[index][field] = value;
    setCourses(newCourses);
  };

  const addCourse = () => {
    setCourses([...courses, { name: '', grade: 'A', credits: '' }]);
  };

  const removeCourse = (index) => {
    if (courses.length > 1) {
      const newCourses = courses.filter((_, i) => i !== index);
      setCourses(newCourses);
    }
  };

  const calculateGPA = () => {
    let totalPoints = 0;
    let totalCredits = 0;

    courses.forEach(course => {
      const credits = parseFloat(course.credits);
      if (credits > 0) {
        totalPoints += gradePoints[course.grade] * credits;
        totalCredits += credits;
      }
    });

    if (totalCredits > 0) {
      setResult({
        gpa: (totalPoints / totalCredits).toFixed(2),
        totalCredits: totalCredits,
        totalPoints: totalPoints.toFixed(2)
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/other-calculators" className="text-cyan-400 hover:text-cyan-300">Other Calculators</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">GPA Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-2">GPA Calculator</h1>
          <p className="text-gray-400 mb-8">
            Calculate your Grade Point Average (GPA) by entering your courses, grades, and credit hours.
          </p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="grid grid-cols-12 gap-3 mb-3 font-medium text-gray-400 text-sm">
              <div className="col-span-5">Course Name (Optional)</div>
              <div className="col-span-3">Grade</div>
              <div className="col-span-3">Credits</div>
              <div className="col-span-1"></div>
            </div>

            {courses.map((course, index) => (
              <div key={index} className="grid grid-cols-12 gap-3 mb-3">
                <div className="col-span-5">
                  <Input
                    type="text"
                    value={course.name}
                    onChange={(e) => updateCourse(index, 'name', e.target.value)}
                    placeholder={`Course ${index + 1}`}
                    className="w-full bg-[#0d0d0d] border-[#2a2a2a] text-white"
                  />
                </div>
                <div className="col-span-3">
                  <select
                    value={course.grade}
                    onChange={(e) => updateCourse(index, 'grade', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg text-white"
                  >
                    {Object.keys(gradePoints).map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    value={course.credits}
                    onChange={(e) => updateCourse(index, 'credits', e.target.value)}
                    placeholder="3"
                    className="w-full bg-[#0d0d0d] border-[#2a2a2a] text-white"
                    min="0"
                    step="0.5"
                  />
                </div>
                <div className="col-span-1 flex items-center justify-center">
                  <button
                    onClick={() => removeCourse(index)}
                    className="text-red-400 hover:text-red-300 text-xl font-bold transition-colors"
                    disabled={courses.length === 1}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}

            <div className="flex gap-4 mt-5">
              <Button onClick={addCourse} variant="outline" className="border-[#2a2a2a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white">
                + Add Course
              </Button>
              <Button onClick={calculateGPA} className="bg-cyan-600 hover:bg-cyan-500 text-white">
                Calculate GPA
              </Button>
            </div>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <h3 className="text-lg font-semibold text-white mb-4">Results</h3>
                
                <div className="p-5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-center mb-4">
                  <span className="text-gray-400">Your GPA</span>
                  <p className="text-5xl font-bold text-cyan-400">{result.gpa}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-[#1a1a1a] rounded-lg text-center">
                    <span className="text-gray-500 text-sm">Total Credits</span>
                    <p className="font-semibold text-white text-lg">{result.totalCredits}</p>
                  </div>
                  <div className="p-4 bg-[#1a1a1a] rounded-lg text-center">
                    <span className="text-gray-500 text-sm">Total Grade Points</span>
                    <p className="font-semibold text-white text-lg">{result.totalPoints}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4">Grade Point Scale</h2>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {Object.entries(gradePoints).map(([grade, points]) => (
                <div key={grade} className="p-3 bg-[#0d0d0d] rounded-lg text-center">
                  <span className="font-semibold text-white">{grade}</span>
                  <span className="text-gray-500 ml-2">= {points.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GPACalculatorPage;
